// @flow
//
// Note: this component is currently used for the school name in the grad
// program form and for cms content article names. It is generalized so other
// Algolia based sources can be used by adding new AutocompleteType options.
import * as React from 'react';
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import * as SS from 'styled-system';
import Autosuggest from 'react-autosuggest';
import algoliaSearch from 'utils/algoliaSearch';
import getSearchConfig from 'utils/search/getSearchConfig';
import once from 'lodash.once';
import type { Invoice } from 'api/staff';
import { fetchInvoices } from 'api/staff';
import { requiredConfig } from 'utils/config';
import { getIn } from 'utils/functional';
import Text from 'components/Text';
import { colors, transition, fontWeights } from '../theme';

const { useState, useMemo, useEffect } = React;

export type AutocompleteType =
  | 'GRAD_SCHOOLS'
  | 'CONTENT'
  | 'NEW_ORG_NAME'
  | 'STAFF_ORG_PAYMENTS'
  | 'SALARY_SURVEY_TITLES'
  | 'STAFF_SALARY_SURVEY_TITLES'
  | 'STAFF_INVOICE_PAYMENTS';

type AutocompleteOptions = {
  queryOptions: {
    index: string,
    attributesToRetrieve: string,
    page: number,
    hitsPerPage: number,
    filters?: string
  },
  responseValue: string,
  responseName: string
};

type AutocompleteOptionsMap = {
  [key: AutocompleteType]: AutocompleteOptions
};

const autocompleteOptionsMap: () => AutocompleteOptionsMap = once(() => ({
  GRAD_SCHOOLS: {
    queryOptions: {
      index: requiredConfig('algolia', 'gradschoolsIndexName'),
      page: 0,
      hitsPerPage: 50,
      attributesToRetrieve: ['name', 'schoolName'].join()
    },
    responseValue: 'schoolName',
    responseName: 'schoolName'
  },
  CONTENT: {
    queryOptions: {
      index: requiredConfig('algolia', 'contentIndexName'),
      page: 0,
      hitsPerPage: 50,
      attributesToRetrieve: ['objectID', 'title'].join()
    },
    responseValue: 'objectID',
    responseName: 'title'
  },
  NEW_ORG_NAME: {
    queryOptions: {
      index: requiredConfig('algolia', 'siteIndexName'),
      page: 0,
      hitsPerPage: 20,
      filters: 'type:ORG',
      attributesToRetrieve: ['objectId', 'name'].join()
    },
    responseValue: null,
    responseName: 'name'
  },
  STAFF_ORG_PAYMENTS: {
    queryOptions: {
      index: requiredConfig('algolia', 'staffIndexName'),
      hitsPerPage: 50,
      page: 0,
      attributesToRetrieve: ['objectID', 'name'].join(),
      filters: 'type:ORG',
      attributesToHighlight: [],
      getRankingInfo: false,
      clickAnalytics: false
    },
    responseValue: 'objectID',
    responseName: 'name'
  },
  SALARY_SURVEY_TITLES: {
    queryOptions: {
      index: requiredConfig('algolia', 'compdbIndexName'),
      page: 0,
      hitsPerPage: 20,
      attributesToRetrieve: ['name', 'jobTitle'].join(),
      removeWordsIfNoResults: 'allOptional'
    },
    responseValue: null,
    responseName: 'name'
  },
  STAFF_SALARY_SURVEY_TITLES: {
    queryOptions: {
      index: requiredConfig('algolia', 'compDbStaffIndexName'),
      page: 0,
      hitsPerPage: 50,
      attributesToRetrieve: ['name', 'subFamilyId'].join()
    },
    responseValue: null,
    responseName: 'name'
  }
}));

const AutosuggestWrapper = styled.div`
  position: relative;

  .react-autosuggest {

    &__input {
      margin: 0;
      padding: 11px 1em;
      font: inherit;
      font-size: 18px;
      color: ${colors.charcoalGrey};
      border-radius: 4px;
      border: solid 1px;
      border-color: ${p => (p.hasError ? colors.scarlet : colors.silver)};
      height: ${({ height }) => height || '45px'};
      width: 100%;
      background-color: ${colors.white};
      transition: all ${transition};

      ::placeholder {
        color: ${colors.middleGrey};
      }

      &:focus {
        border-color: ${colors.waterBlue};
        box-shadow: 0 0 0 1px ${colors.waterBlue};
        outline: 0;
      }

      &:disabled {
        background-color: ${colors.lightGrey};
      }

      ${SS.width} ${SS.height} ${SS.color} ${SS.space} ${SS.fontSize}
      ${SS.borders} ${SS.borderRadius} ${SS.borderColor};

      ${props =>
        props.hasError &&
        `
        border-color: ${colors.scarlet};
        box-shadow: 0 0 0 1px ${colors.scarlet};
      `}
    }

    &__suggestions-container {
      display: none;

      &--open {
        display: block;
        margin-top: 4px;
        position: absolute;
        width: 100%;
        overflow-y: scroll;
        max-height: 400px;
        border: 1px solid;
        border-color: ${colors.silver};
        background-color: #fff;
        font-size: 18px;
        line-height: 19px;
        border-radius: 4px;
        z-index: 2;
      }
    }

    &__suggestions-list {
      margin: 0;
      padding: 0 ${({ borderSize }) => borderSize}px;
      list-style-type: none;
    }

    &__suggestion {
      cursor: pointer;
      padding: ${props => (props.large ? '16px 24px' : '10px 1em')};
      transition: all ${transition};
      ${({ borderSize }) => `
        box-shadow: 0 -${borderSize}px rgba(255,255,255,0) inset, 0 ${borderSize}px rgba(255,255,255,0) inset;
      `}

      &:not(:first-child) {
        border-top: 1px dashed ${colors.aluminum};
      }

      &--highlighted {
        background-color: ${colors.offWhite};
        ${({ borderSize }) => `
          box-shadow: 0 -${borderSize}px white inset, 0 ${borderSize}px white inset;
        `}
      }
    }
  }
`;

type Response = {
  hits: Array<Object>
};

const getQueryOptions = (
  autocompleteType: AutocompleteType,
  filters?: string
) => ({
  ...autocompleteOptionsMap()[autocompleteType].queryOptions,
  filters:
    filters ||
    autocompleteOptionsMap()[autocompleteType].queryOptions.filters ||
    ''
});

const responseToOption = (
  response: Response,
  autocompleteType: AutocompleteType
) => {
  const { responseValue, responseName } = autocompleteOptionsMap()[
    autocompleteType
  ];
  const results = response.hits.map(hit => ({
    value: responseValue ? getIn(hit, responseValue.split('.')) : hit,
    name: getIn(hit, responseName.split('.'))
  }));
  return results;
};

const getSuggestionValue = suggestion => suggestion.name;

const promiseOptions = (
  input,
  autocompleteType,
  filters,
  overrideSearchConfig?: $ReadOnly<{ apiKey: string, appId: string }>
) => {
  if (autocompleteType === 'STAFF_INVOICE_PAYMENTS') {
    return fetchInvoices({ number: input, outstanding: true }).then(invoices =>
      invoices.map<{ value: string, name: string, invoice: Invoice }>(
        (i: Invoice) => ({
          value: i.id,
          name: i.number.toString(),
          invoice: i
        })
      )
    );
  }
  return algoliaSearch(
    overrideSearchConfig || getSearchConfig(),
    input,
    getQueryOptions(autocompleteType, filters)
  ).then((r: Response) => responseToOption(r, autocompleteType));
};

type Props = {
  onChange: Object => void,
  onBlur?: Function,
  onSuggestionSelected?: Function,
  onSuggestionsUpdated?: Function,
  shouldRenderSuggestions?: Function,
  autocompleteType: AutocompleteType,
  filterOptions?: string,
  hasError: boolean,
  searchConfig?: { apiKey: string, appId: string },
  large?: boolean,
  value: any,
  placeholder?: string
};

const AutocompleteTextInput = ({
  onChange,
  onBlur,
  onSuggestionSelected,
  onSuggestionsUpdated,
  shouldRenderSuggestions,
  autocompleteType,
  filterOptions,
  hasError,
  searchConfig: overrideSearchConfig,
  large,
  placeholder,
  ...otherProps
}: Props) => {
  const [suggestions, updateSuggestions] = useState<
    $ReadOnlyArray<{ value: string, name: string }>
  >([]);
  const [inProgressQuery, updateInProgressQuery] = useState<?Promise<
    $ReadOnlyArray<{ name: string, value: string }>
  >>(null);

  useEffect(
    () => {
      if (onSuggestionsUpdated) {
        onSuggestionsUpdated({ suggestions, value: otherProps.value });
      }
    },
    [onSuggestionsUpdated, otherProps.value, suggestions]
  );

  const loadSuggestions = (value: string) => {
    // Cancel the previous request
    if (inProgressQuery) {
      inProgressQuery.then(() => {
        debounce(() => loadSuggestions(value));
      });
    } else {
      const query = promiseOptions(
        value,
        autocompleteType,
        filterOptions,
        overrideSearchConfig
      );
      query.then(options => {
        updateSuggestions(options);
        updateInProgressQuery(null);
      });
      updateInProgressQuery(query);
      return query;
    }
  };

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    loadSuggestions(value);
  };

  const onSuggestionsClearRequested = () => {
    updateSuggestions([]);
    updateInProgressQuery(null);
  };

  const onChangeInternal = (
    event: Object,
    { newValue }: { newValue: string }
  ) => {
    onChange({ target: { value: newValue } });
  };

  const onBlurInternal = (event: Object) => {
    if (onBlur) {
      onBlur(event, suggestions);
    }
  };

  const renderSuggestion = useMemo(
    () => suggestion => (
      <Text fontWeight={large && fontWeights.semiBold}>{suggestion.name}</Text>
    ),
    [large]
  );

  const inputProps = {
    ...otherProps,
    placeholder,
    onChange: onChangeInternal,
    onBlur: onBlurInternal
  };

  return (
    <AutosuggestWrapper
      large={large}
      borderSize={large ? 4 : 2}
      hasError={hasError}
    >
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={shouldRenderSuggestions}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
      />
    </AutosuggestWrapper>
  );
};

export default AutocompleteTextInput;
